import { useState, useEffect } from "react";
import { Type } from "@themes/pollinator/ui/Type";
import { useDevices } from "../../../providers/DevicesProvider";
import { usePlatformStore, useVideoMeetingDetails } from "@platform/store";
import SpeakerSelection from "./SpeakerSelection";
import TestSpeakersButton from "./TestSpeakersButton";
import { Button } from "@themes/pollinator/ui/Button";
import { RefreshCw } from "lucide-react";
import { logVideoRoomError } from "../../../utils/logVideoRoomError";

// Helper function to detect iOS devices
const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
};

const SpeakerDevices = () => {
  const {
    selectedAudioOutputDevice,
    selectAudioOutputDevice,
    forceRefreshDevices,
    audioOutputDevices,
    isDevicesLoading,
  } = useDevices();
  const { roomId } = useVideoMeetingDetails();
  const user = usePlatformStore((s) => s.user);
  const [selectedOutput, setSelectedOutput] = useState(
    selectedAudioOutputDevice
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showNoSpeakersMessage, setShowNoSpeakersMessage] = useState(false);
  const [hasLoggedError, setHasLoggedError] = useState(false);
  // Number of times Refresh Devices has been clicked without any new
  // device appearing. Drives the post-refresh "still nothing — try OS
  // settings + browser restart" hint.
  const [emptyRefreshAttempts, setEmptyRefreshAttempts] = useState(0);
  const isIOS = isIOSDevice();
  
  // SPOOF: Force no speakers for testing error logging
  const [spoofNoSpeakers] = useState(() => {
    // Check URL parameters for spoofing
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('spoofNoSpeakers') === 'true';
  });
  
  // SPOOF: Override audioOutputDevices to be empty when spoofing
  const effectiveAudioOutputDevices = spoofNoSpeakers ? [] : audioOutputDevices;

  const logError = (errorMessage: string, errorType: string, additionalData?: Record<string, unknown>) =>
    logVideoRoomError(errorMessage, errorType, additionalData, { roomId, user });

  // Update local state when the device provider's selection changes
  useEffect(() => {
    setSelectedOutput(selectedAudioOutputDevice);
  }, [selectedAudioOutputDevice]);

  // Handle delayed display of no speakers message
  useEffect(() => {
    // Skip message for iOS devices due to known speaker detection limitations
    if (isIOS && !spoofNoSpeakers) {
      setShowNoSpeakersMessage(false);
      return;
    }

    // Reset the message when devices are loading
    if (isDevicesLoading) {
      setShowNoSpeakersMessage(false);
      return;
    }

    // If devices are not loading and we have no speakers, show message after a delay
    if (!isDevicesLoading && effectiveAudioOutputDevices.length === 0) {
      const timer = setTimeout(() => {
        setShowNoSpeakersMessage(true);
        
        // Log error when no speakers are detected
        if (!hasLoggedError) {
          logError(
            "No speaker devices detected",
            "no_speakers_detected",
            {
              spoofed: spoofNoSpeakers,
              isIOS: isIOS,
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString()
            }
          );
          setHasLoggedError(true);
        }
      }, spoofNoSpeakers ? 500 : 2000); // Faster display when spoofing

      return () => clearTimeout(timer);
    } else {
      // If we have speakers, hide the message and reset error logging flag
      setShowNoSpeakersMessage(false);
      setHasLoggedError(false);
    }
  }, [isDevicesLoading, effectiveAudioOutputDevices.length, isIOS, spoofNoSpeakers]);

  const handleChange = async (deviceId: string): Promise<void> => {
    try {
      // Update our custom device provider
      await selectAudioOutputDevice(deviceId);
      setSelectedOutput(deviceId);
    } catch {
      // Non-fatal: audio output device selection failed
    }
  };

  const handleRefreshDevices = async () => {
    const beforeCount = effectiveAudioOutputDevices.length;
    setIsRefreshing(true);
    const startedAt = Date.now();
    try {
      await forceRefreshDevices();
    } catch {
      // Non-fatal: device refresh failed
    }
    // Hold the spinner state long enough for it to be visible. Without
    // this, enumerateDevices resolves in single-digit milliseconds and
    // the click looks like a no-op.
    const MIN_VISIBLE_SPINNER_MS = 600;
    const elapsed = Date.now() - startedAt;
    if (elapsed < MIN_VISIBLE_SPINNER_MS) {
      await new Promise((r) => setTimeout(r, MIN_VISIBLE_SPINNER_MS - elapsed));
    }
    // Track empty-result refreshes so the panel can show a hint about
    // the most likely root cause (OS-level Privacy gate + browser
    // restart, which a per-page refresh can't recover from).
    setEmptyRefreshAttempts((n) =>
      effectiveAudioOutputDevices.length === beforeCount &&
      effectiveAudioOutputDevices.length === 0
        ? n + 1
        : 0
    );
    setIsRefreshing(false);
  };

  return (
    <>
      {/* SPOOF: Show indicator when spoofing is active */}
      {spoofNoSpeakers && (
        <div className="flex items-center gap-2 p-2 mb-3 border border-orange-300 rounded-md bg-orange-50">
          <div className="w-4 h-4 text-orange-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <Type className="text-orange-700" variant="caption">
            🧪 TESTING MODE: Speaker detection spoofed for error logging testing
          </Type>
          <Button
            onClick={() => {
              logError(
                "Manual test error from spoofed speaker detection",
                "manual_test_error",
                {
                  spoofed: true,
                  manualTrigger: true,
                  timestamp: new Date().toISOString()
                }
              );
            }}
            variant="secondary"
            size="sm"
          >
            Test Error Log
          </Button>
        </div>
      )}
      
      <SpeakerSelection 
        onChange={handleChange} 
        devices={effectiveAudioOutputDevices}
        selectedDevice={selectedOutput}
      />
      <TestSpeakersButton speakerOutput={selectedOutput || ""} />

      {isDevicesLoading && (
        <div className="flex items-center gap-2 p-3 border border-brand-700 rounded-lg">
          <div className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin"></div>
          <Type className="text-brand-700" variant="caption">
            Detecting speaker devices...
          </Type>
        </div>
      )}

      {showNoSpeakersMessage && (
        <div className="flex flex-col gap-3 p-3 border border-error-300 rounded-md bg-error-50">
          <div className="flex-1 flex flex-col gap-2 p-2">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 text-error-600 mt-0.5">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <Type variant="h5" className="text-error-700">
                No speakers found
              </Type>
            </div>
            <Type variant="caption" className="text-error-700">
            Your browser is most likely using your default speaker. To confirm this, click the "Test speakers" button, if you hear the tones, then your audio should be okay in the meeting. If not, please refresh or try a different browser.
            </Type>
            <div className="space-y-2">
              <Type className=" text-error-700" variant="caption">
              If you don't hear the tones:
              </Type>
              <Type
                className="text-error-700 list-disc list-inside"
                variant="caption"
                as="ul"
              >
                <li>Reload the page to refresh device detection.</li>
                <li>Try a different browser (Chrome, Firefox, Safari).</li>
                <li>
                  Check if your speakers/headphones are properly connected.
                </li>
                <li>
                  Ensure your browser has permission to access audio devices.
                </li>
              </Type>
            </div>
          </div>
          {emptyRefreshAttempts > 0 && (
            <div className="px-2">
              <Type variant="caption" className="text-error-700">
                Refresh ran but no new devices were detected. The most
                common cause is that your OS has blocked your browser from
                accessing audio devices. On Mac: System Settings → Privacy
                &amp; Security → Microphone → enable for your browser,
                then <strong>quit and reopen the browser</strong> (a page
                reload alone won't pick up OS-level changes).
              </Type>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              onClick={handleRefreshDevices}
              disabled={isRefreshing}
              variant="secondary"
              iconComponent={RefreshCw}
            >
              {isRefreshing ? "Refreshing..." : "Refresh Devices"}
            </Button>
            <Button
              onClick={() => window.location.reload()}
              variant="secondary"
            >
              Reload Page
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default SpeakerDevices;
